// useMapPins.js

import { useMemo } from 'react';

import { useNodeTypes } from 'context/NodeTypesContext';

import { resolveDisplaySchemaFields } from 'views/utils/displaySchemaFields';

import { resolveSchemaFieldValue } from 'views/utils/fieldValueResolver';

import { extractNodeLocationPins } from '../utils/mapLocationResolver';

const useMapPins = ({ nodes = [], typeDef = null }) => {
  const { getRevision } = useNodeTypes();
  const nodeTypesRevision = getRevision();

  const pins = useMemo(() => {
    const list = Array.isArray(nodes) ? nodes : [];
    if (!list.length) return [];

    const locationFieldsByType = new Map();

    const getLocationFields = (nodeTypeKey) => {
      const key = String(nodeTypeKey || '').trim();
      if (!key) return [];
      const cacheKey = `${key}:${nodeTypesRevision}`;
      if (locationFieldsByType.has(cacheKey)) return locationFieldsByType.get(cacheKey);

      const fields = (typeDef?.key === key)
        ? resolveDisplaySchemaFields(typeDef, { includeHidden: true })
        : resolveDisplaySchemaFields({ key }, { includeHidden: true });
      const locationFields = (Array.isArray(fields) ? fields : []).filter((field) => (
        String(field?.type || '').toLowerCase() === 'location'
      ));

      locationFieldsByType.set(cacheKey, locationFields);
      return locationFields;
    };

    const allPins = [];

    list.forEach((node) => {
      const nodeTypeKey = String(node?.type || typeDef?.key || '').trim();
      if (!nodeTypeKey) return;

      const locationFields = getLocationFields(nodeTypeKey);
      if (!locationFields.length) return;

      const resolveFieldValue = (item, fieldDef) => resolveSchemaFieldValue({
        item,
        fieldDef,
        typeKey: nodeTypeKey,
        dataSection: typeDef?.dataSection,
      });

      allPins.push(
        ...extractNodeLocationPins({
          node,
          locationFields,
          resolveFieldValue,
        })
      );
    });

    return allPins;
  }, [
    nodes,
    typeDef,
    nodeTypesRevision,
  ]);

  return { pins };
};

export default useMapPins;
